/**
 * End-to-end encryption utilities.
 * Uses PBKDF2 for key derivation and AES-GCM for encryption.
 * Data is encrypted client-side before being sent to the server.
 */

// OWASP 2023+ recommends 600,000 iterations for PBKDF2-SHA256.
// Legacy records used 100,000; deriveKey accepts an explicit iterations
// arg so we can decrypt old data while issuing new keys at full strength.
export const PBKDF2_ITERATIONS = 600_000
export const LEGACY_PBKDF2_ITERATIONS = 100_000
const KEY_LENGTH = 256 // bits
const IV_LENGTH = 12 // bytes

export interface EncryptedPayload {
  iv: string        // hex-encoded 12-byte IV
  ciphertext: string // base64-encoded ciphertext
}

/** Convert Uint8Array to hex string */
function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/** Convert hex string to Uint8Array */
function fromHex(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return bytes
}

/** Convert ArrayBuffer to base64 string */
function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/** Convert base64 string to ArrayBuffer */
function fromBase64(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/** Generate a random encryption salt (16 bytes, returned as hex) */
export function generateEncryptionSalt(): string {
  return toHex(crypto.getRandomValues(new Uint8Array(16)))
}

/**
 * Derive an AES-GCM encryption key from a password and salt.
 * Pass `iterations` to match the strength a particular record was encrypted
 * at (defaults to the current strong value for new encryptions).
 */
export async function deriveKey(
  password: string,
  saltHex: string,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<CryptoKey> {
  const encoder = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  )

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: fromHex(saltHex),
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  )
}

/** Encrypt a plaintext string with AES-GCM */
export async function encrypt(plaintext: string, key: CryptoKey): Promise<EncryptedPayload> {
  const encoder = new TextEncoder()
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  )

  return {
    iv: toHex(iv),
    ciphertext: toBase64(ciphertext),
  }
}

/** Decrypt an AES-GCM encrypted payload */
export async function decrypt(payload: EncryptedPayload, key: CryptoKey): Promise<string> {
  const decoder = new TextDecoder()

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromHex(payload.iv) },
    key,
    fromBase64(payload.ciphertext)
  )

  return decoder.decode(plaintext)
}
